import { createSlice } from '@reduxjs/toolkit';
import { RootState } from '$store/rootReducer';
import {
  AddMessageAction,
  AddMessagesAction,
  DeleteMessageAction,
  GetOnlineUsersAction,
  GetTotalTipsAction,
  LiveHistoryFilterEnum,
  LivePermissionEnum,
  LiveState,
  LiveStatus,
  LiveType,
  RemoveStreamAction,
  SetCamerasAction,
  SetConfirmStopAction,
  SetEndStatisticsActionPayload,
  SetFocusAction,
  SetHistoryAction,
  SetHistoryFilterAction,
  SetLiveStatusAction,
  SetMediaStreamAction,
  SetMessageAction,
  SetMicEnabledAction,
  SetOnlineUsersAction,
  SetOnlineUsersLoadingStatusAction,
  SetOnlineUsersSheetActions,
  SetPermissionAction,
  SetReconnectingAction,
  SetStreamAction,
  SetTipsAction,
  SetStreamsAction,
  SetTipsTotalActions,
  SetTipsTotalSheetActions,
  SetTogglingCameraAction,
  SetTotalTipsLoadingStatusAction,
  SetTypeAction,
  ShowActionsSheetAction,
  startWatchLiveAction,
  UpdateMessageAction,
} from '$store/live/interface';
import { CameraTypeEnum } from '$shared/constants';
import { getCameraTypeByLabel } from '$utils/camera';
import { AttachType } from '$store/models';

const initialState: LiveState = {
  historyFilter: LiveHistoryFilterEnum.ALL,
  streams: [],
  type: LiveType.VIEWER,
  tipsTotal: 0,
  permission: LivePermissionEnum.PUBLIC,
  confirmStop: false,
  stream: null,
  mediaStream: null,
  cameras: [],
  currentCameraId: null,
  liveStatus: LiveStatus.INIT,
  messages: [],
  tipsMessagesCount: 0,
  online: 0,
  sponsors: {},
  nextFrom: 0,
  allMessagesLoaded: false,
  micEnabled: true,
  message: '',
  focus: false,
  reconnecting: false,
  togglingCamera: false,
  cameraName: '',
  cameraType: CameraTypeEnum.USER,
  actionsSheetUserId: null,
  endStatistics: undefined,
  showUsersSheet: false,
  showTotalTipsSheet: false,
  tips: [],
  onlineUsers: [],
  totalTipsLoadingStatus: 'loaded',
  onlineUsersLoadingStatus: 'loaded',
};

export const { actions, reducer } = createSlice({
  name: 'live',
  initialState,
  reducers: {
    initLive() {},
    setHistoryFilter(state, action: SetHistoryFilterAction) {
      state.historyFilter = action.payload;
    },
    showActionsSheet(state, action: ShowActionsSheetAction) {
      state.actionsSheetUserId = action.payload;
    },
    hideActionsSheet(state) {
      state.actionsSheetUserId = null;
    },
    setTips(state, action: SetTipsAction) {
      state.tips = action.payload;
    },
    stopHistoryLoop() {},
    reconnect() {},
    toggleMic(state) {
      state.micEnabled = !state.micEnabled;
    },
    setReconnecting(state, action: SetReconnectingAction) {
      state.reconnecting = action.payload;
    },
    setTogglingCamera(state, action: SetTogglingCameraAction) {
      state.reconnecting = action.payload;
    },
    setMicEnabled(state, action: SetMicEnabledAction) {
      state.micEnabled = action.payload;
    },
    setStreams(state, action: SetStreamsAction) {
      state.streams = action.payload;
    },
    sendMessage(state) {
      state.historyFilter = LiveHistoryFilterEnum.ALL;
    },
    setMessage(state, action: SetMessageAction) {
      state.message = action.payload;
    },
    setFocus(state, action: SetFocusAction) {
      state.focus = action.payload;
    },
    deleteMessage(state, action: DeleteMessageAction) {
      state.messages = state.messages.filter((m) => m.id !== action.payload);
    },
    clear(state) {
      state.online = initialState.online;
      state.mediaStream = initialState.mediaStream;
      state.stream = initialState.stream;
      state.liveStatus = initialState.liveStatus;
      state.message = initialState.message;
      state.messages = initialState.messages;
      state.nextFrom = initialState.nextFrom;
      state.permission = initialState.permission;
      state.reconnecting = initialState.reconnecting;
      state.allMessagesLoaded = initialState.allMessagesLoaded;
      state.micEnabled = initialState.micEnabled;
      state.tipsTotal = initialState.tipsTotal;
      state.tipsMessagesCount = initialState.tipsMessagesCount;
      state.type = initialState.type;
      state.endStatistics = initialState.endStatistics;
    },
    setPermission(state, action: SetPermissionAction) {
      state.permission = action.payload;
    },
    setConfirmStop(state, action: SetConfirmStopAction) {
      state.confirmStop = action.payload;
    },
    setType(state, action: SetTypeAction) {
      state.type = action.payload;
    },
    setTotalTips(state, action: SetTipsTotalActions) {
      state.tipsTotal = action.payload;
    },
    banned(state) {
      state.liveStatus = LiveStatus.BANNED;
    },
    setLiveStatus: (state, action: SetLiveStatusAction) => {
      if (!state.reconnecting) {
        state.liveStatus = action.payload;
      }
      if (action.payload === LiveStatus.ONLINE) {
        state.reconnecting = false;
      }
    },
    getTotalTips(_, action: GetTotalTipsAction) {},
    setTotalTipsSheet(state, action: SetTipsTotalSheetActions) {
      state.showTotalTipsSheet = action.payload;
    },
    setOnlineUsersSheet(state, action: SetOnlineUsersSheetActions) {
      state.showUsersSheet = action.payload;
    },
    setTotalTipsLoadingStatus(state, action: SetTotalTipsLoadingStatusAction) {
      state.totalTipsLoadingStatus = action.payload;
    },
    getOnlineUsers(_, action: GetOnlineUsersAction) {},
    setOnlineUsers(state, action: SetOnlineUsersAction) {
      state.onlineUsers = action.payload;
    },
    setOnlineUsersLoadingStatus(state, action: SetOnlineUsersLoadingStatusAction) {
      state.onlineUsersLoadingStatus = action.payload;
    },
    removeStream(state, action: RemoveStreamAction) {
      state.streams = state.streams.filter((s) => s.id !== action.payload);
    },
    setHistory(state, action: SetHistoryAction) {
      const { payload } = action;
      const messageIds = state.messages.map((m) => m.id);
      if (!state.allMessagesLoaded && action.payload.messages.length === 0) {
        state.allMessagesLoaded = true;
      }
      state.messages.push(...payload.messages.filter((m) => !messageIds.includes(m.id)));
      state.nextFrom = payload.nextFrom;
      state.online = payload.online;
      state.tipsTotal = payload.tipsTotal;
      state.sponsors = {
        ...state.sponsors,
        ...payload.sponsors,
      };
      state.tipsMessagesCount = state.messages.filter((m) =>
        m.attachments.find((a) => a.type === AttachType.TIPS),
      ).length;
    },
    addMessage(state, action: AddMessageAction) {
      state.messages.push(action.payload);
    },
    updateMessage(state, action: UpdateMessageAction) {
      const { id, message } = action.payload;
      const index = state.messages.findIndex((m) => m.id === id);
      if (index !== -1) {
        state.messages[index] = message;
      }
    },
    setMediaStream(state, action: SetMediaStreamAction) {
      state.mediaStream = action.payload;
    },
    setStream(state, action: SetStreamAction) {
      state.stream = action.payload;
    },
    setCameras(state, action: SetCamerasAction) {
      state.cameras = action.payload;
      const firstCamera = action.payload[0];
      state.cameraType = getCameraTypeByLabel(firstCamera.label);
      state.currentCameraId = firstCamera.deviceId;
    },
    // setCameraId(state, action: SetCameraIdAction) {
    //   state.currentCameraId = action.payload;
    // },
    toggleCamera(state) {
      // TODO: toggle only USER and ENVIRONMENT cameras
      if (state.currentCameraId) {
        const index = state.cameras.findIndex(
          (item) => item.deviceId === state.currentCameraId,
        );
        const newCamera = state.cameras[(index + 1) % state.cameras.length];
        state.cameraType = getCameraTypeByLabel(newCamera.label);
        console.log(newCamera.label, state.cameraType);
        state.currentCameraId = newCamera.deviceId;
        state.togglingCamera = true;
      }
    },
    startWatchLive(state, action: startWatchLiveAction) {
      // set status
    },
    stopWatchLive() {
      // set status
    },
    startStream() {},
    stopStream() {},
    createLive() {},
    stopLive(state) {
      state.confirmStop = false;
    },
    setEndStatistics(state, action: SetEndStatisticsActionPayload) {
      state.endStatistics = action.payload;
    },
    closeLiveEndStatistics(state) {},
  },
});

export { reducer as liveReducer, actions as liveActions };

export const liveSelector = (state: RootState) => state.live;
export const liveActionsSheetUserIdSelector = (state: RootState) =>
  state.live.actionsSheetUserId;
export const liveStreamSelector = (state: RootState) => state.live.stream;
export const liveMediaStreamSelector = (state: RootState) => state.live.mediaStream;
export const liveStreamsSelector = (state: RootState) => state.live.streams;
export const liveTipsSelector = (state: RootState) => state.live.tips;
export const liveTipsTotalSelector = (state: RootState) => state.live.tipsTotal;
export const liveOnlineUsersSelector = (state: RootState) => state.live.onlineUsers;
export const LiveOnlineUsersLoadingStatusSelector = (state: RootState) =>
  state.live.onlineUsersLoadingStatus;
export const liveTotalTipsLoadingStatusSelector = (state: RootState) =>
  state.live.totalTipsLoadingStatus;
export const liveShowTotalTipsSheetSelector = (state: RootState) =>
  state.live.showTotalTipsSheet;
export const liveShowUsersSheetSelector = (state: RootState) => state.live.showUsersSheet;
export const LiveShowTotalTipsSheetSelector = (state: RootState) =>
  state.live.showTotalTipsSheet;
